import { DriverBeneficiary } from "@deathstar/types/northstar";
import { DriverBeneficiaryDto } from "@deathstar/types/waypoint";
import { InputField } from "../inputField/inputField";
import { PrimaryButton } from "../primaryButton/primaryButton";
import { SecondaryButton } from "../secondaryButton/secondaryButton";
import { Dialog } from "@material-ui/core";
import { isPostalCode } from "class-validator";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const emptyBeneficiary: DriverBeneficiaryDto = {
    name: "",
    relationship: "" as DriverBeneficiary.Relationship,
    street: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
};

export function BeneficiaryDialog({
    open,
    currentValue,
    onChange,
    onClose,
}: {
    open: boolean;
    currentValue: DriverBeneficiaryDto | null;
    onChange(value: DriverBeneficiaryDto | null): unknown;
    onClose(): void;
}) {
    const form = useForm<DriverBeneficiaryDto>({ defaultValues: emptyBeneficiary });

    useEffect(() => {
        if (open) {
            form.reset(currentValue ? { ...currentValue, street2: currentValue.street2 || "" } : emptyBeneficiary);
        }
    }, [open, currentValue, form]);

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: "p-8 w-screen max-w-lg" }}>
            <form
                className="grid grid-cols-6 gap-4 text-sm"
                onSubmit={(e) => {
                    // This dialog is rendered inside the driver form, so don't let the submit bubble to it
                    e.stopPropagation();
                    form.handleSubmit(({ name, relationship, street, street2, city, state, zip }) => {
                        // Only send the editable fields (no row id), so pending-change comparison sees edits to a saved beneficiary
                        onChange({ name, relationship, street, street2: street2 || null, city, state: state.trim().toUpperCase(), zip });
                        onClose();
                    })(e);
                }}
            >
                <p className="col-span-full text-lg font-medium">Beneficiary</p>
                <InputField
                    classes={{ root: "col-span-full" }}
                    label="Name"
                    placeholder="Jane Doe"
                    {...form.register("name", { required: "Please enter a name", maxLength: 128 })}
                    required
                    autoFocus
                    error={!!form.formState.errors?.name}
                    helperText={form.formState.errors?.name?.message}
                />
                <div className="col-span-full flex flex-col">
                    <label htmlFor="beneficiary-relationship">
                        Relationship <span className="text-red-600">*</span>
                    </label>
                    <select
                        id="beneficiary-relationship"
                        className="form-select rounded border-stone-300 text-sm"
                        {...form.register("relationship", { required: "Please select a relationship" })}
                    >
                        <option value="" disabled>
                            Select a relationship
                        </option>
                        {Object.values(DriverBeneficiary.Relationship).map((relationship) => (
                            <option key={relationship} value={relationship}>
                                {relationship}
                            </option>
                        ))}
                    </select>
                    {form.formState.errors?.relationship && <p className="text-red-600">{form.formState.errors.relationship.message}</p>}
                </div>
                <InputField
                    classes={{ root: "col-span-full" }}
                    label="Street"
                    placeholder="100 N. Capitol Ave."
                    {...form.register("street", { required: true, maxLength: 64 })}
                    required
                    error={!!form.formState.errors?.street}
                    helperText={form.formState.errors?.street?.message}
                />
                <InputField
                    classes={{ root: "col-span-full" }}
                    label="Street 2"
                    placeholder="Apt. 2"
                    {...form.register("street2", { maxLength: 64 })}
                    error={!!form.formState.errors?.street2}
                    helperText={form.formState.errors?.street2?.message}
                />
                <InputField
                    classes={{ root: "col-span-3" }}
                    label="City"
                    placeholder="Lansing"
                    {...form.register("city", { required: true, maxLength: 32 })}
                    required
                    error={!!form.formState.errors?.city}
                    helperText={form.formState.errors?.city?.message}
                />
                <InputField
                    classes={{ root: "col-span-3" }}
                    label="State/Province"
                    placeholder="MI"
                    {...form.register("state", {
                        required: "Please enter a state code",
                        maxLength: 2,
                        setValueAs: (value) => (typeof value === "string" ? value.trim().toUpperCase() : value),
                        validate: (value) => /^[A-Z]{2}$/.test(value || "") || "Please enter a 2-letter state code",
                    })}
                    required
                    error={!!form.formState.errors?.state}
                    helperText={form.formState.errors?.state?.message}
                />
                <InputField
                    classes={{ root: "col-span-3" }}
                    label="Postal code"
                    placeholder="48933"
                    {...form.register("zip", { required: true, validate: (v) => isPostalCode(v, "any") || "Invalid postal code" })}
                    required
                    error={!!form.formState.errors?.zip}
                    helperText={form.formState.errors?.zip?.message}
                />
                <div className="col-span-full flex justify-center gap-4">
                    {currentValue && (
                        <SecondaryButton
                            type="button"
                            onClick={() => {
                                onChange(null);
                                onClose();
                            }}
                        >
                            Remove beneficiary
                        </SecondaryButton>
                    )}
                    <PrimaryButton type="submit">Save beneficiary</PrimaryButton>
                </div>
            </form>
        </Dialog>
    );
}
